import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { formatDueDaysForDisplay } from 'shared/utils/invoicing';

import {
    CollapsibleContainer,
    ConnectProviderWarningModal,
} from '@/components';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { ROUTE } from '@/router/routes';

const buttonStyles = {
    alignSelf: 'center',
    minWidth: 175,
};

function InvoicingAndAdministrationSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { isInvoicingAvailable, isAccountingProviderAvailable } =
        useInvoicingSystemContext();
    const { userOrganisation } = useOrganisationsContext();

    const defaultDueDaysDisplay = formatDueDaysForDisplay(
        userOrganisation?.default_invoice_due_days,
    );

    const [isProviderWarningModalVisible, setProviderModalWarningVisible] =
        useState(false);

    const handleConnectToProviderClick = () => {
        isInvoicingAvailable
            ? navigate(ROUTE.accountingProviders)
            : setProviderModalWarningVisible(true);
    };

    const handleProceedClick = () => {
        setProviderModalWarningVisible(false);
        navigate(ROUTE.accountingProviders);
    };

    const handleCancelClick = () => {
        setProviderModalWarningVisible(false);
    };

    const renderSection = ({
        headerText,
        explanationText,
        actionText,
        testId,
        route,
        onClick,
    }: {
        headerText: string;
        explanationText: string;
        actionText: string;
        testId: string;
        route?: string;
        onClick?: () => void;
    }) => (
        <Box sx={{ display: 'flex', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    flexDirection: 'column',
                    flex: 2,
                }}
            >
                <Typography fontWeight="bold" fontSize={14}>
                    {headerText}
                </Typography>
                <Typography fontSize={14}>{explanationText}</Typography>
            </Box>
            <Button
                {...(route ? { component: Link, to: route } : { onClick })}
                variant="contained"
                sx={buttonStyles}
                data-test-id={testId}
            >
                {actionText}
            </Button>
        </Box>
    );

    return (
        <>
            <CollapsibleContainer
                title={t('Options:button:invoicing_administration')}
                testID="SettingsPage-InvoicingAndAdministration"
            >
                <Alert severity="info" sx={{ mb: 3 }}>
                    {t('Settings:invoices_admin:heading_explanation')}
                </Alert>
                {renderSection({
                    headerText: t(
                        'Settings:invoices_admin:configure_procedures_header',
                    ),
                    explanationText: t(
                        'Settings:invoices_admin:configure_procedures_explanation',
                    ),
                    actionText: t(
                        'Settings:invoices_admin:configure_procedures_action',
                    ),
                    testId: 'InvoicingAndAdministrationSection-ConfigureProceduresButton',
                    route: ROUTE.procedures,
                })}
                {isAccountingProviderAvailable && (
                    <>
                        <Divider sx={{ my: 4 }} />
                        {renderSection({
                            headerText: t(
                                'Settings:invoices_admin:connect_provider_header',
                            ),
                            explanationText: t(
                                'Settings:invoices_admin:connect_provider_explanation_xero_only',
                            ),
                            actionText: t(
                                'Settings:invoices_admin:connect_provider_action',
                            ),
                            testId: 'InvoicingAndAdministrationSection-ConnectToProviderButton',
                            onClick: handleConnectToProviderClick,
                        })}
                        <Divider sx={{ my: 4 }} />
                        {renderSection({
                            headerText: t(
                                'Settings:invoices_admin:default_due_days_header',
                            ),
                            explanationText: t(
                                'Settings:invoices_admin:default_due_days_description',
                                {
                                    value: defaultDueDaysDisplay.dueValue,
                                    unit: t(
                                        `Invoice:time_options:${defaultDueDaysDisplay.unit}`,
                                    ),
                                },
                            ),
                            actionText: t(
                                'Settings:invoices_admin:default_due_days_action',
                            ),
                            testId: 'InvoicingAndAdministrationSection-DefaultDueDaysButton',
                            route: ROUTE.defaultInvoiceDueDays,
                        })}
                    </>
                )}
            </CollapsibleContainer>
            {isProviderWarningModalVisible ? (
                <ConnectProviderWarningModal
                    onCancel={handleCancelClick}
                    onProceed={handleProceedClick}
                />
            ) : null}
        </>
    );
}

export default InvoicingAndAdministrationSection;
